import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Calendar, MapPin, LogOut, Star, Clock, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import type { Booking } from "@/types";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { StepProgressBar } from "@/components/tracking/StepProgressBar";
import { FilmmakerBottomNav } from "@/components/navigation/FilmmakerBottomNav";

const statusLabels: Record<string, string> = {
  assigned: 'New Job',
  en_route: 'On the Way',
  arrived: 'At Location',
  in_progress: 'In Progress',
  awaiting_finish_confirm: 'Awaiting Confirmation',
  done_waiting_upload: 'Ready to Upload',
  uploaded: 'Uploaded',
  completed: 'Completed',
};

const statusColors: Record<string, string> = {
  assigned: 'bg-blue-500',
  en_route: 'bg-purple-500',
  arrived: 'bg-green-500',
  in_progress: 'bg-yellow-500',
  awaiting_finish_confirm: 'bg-orange-500',
  done_waiting_upload: 'bg-pink-500',
  uploaded: 'bg-teal-500',
  completed: 'bg-gray-500',
};

export default function FilmmakerDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadBookings = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      // Fetch from Supabase using RLS
      try {
        const { data: bookingsData, error } = await supabase
          .from('bookings')
          .select(`
            *,
            package:packages(*),
            customer:profiles!bookings_customer_id_fkey(
              id,
              full_name,
              phone,
              email
            )
          `)
          .eq('filmmaker_id', user.id)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching filmmaker bookings:', error);
          setBookings([]);
        } else {
          // Loaded bookings for filmmaker
          setBookings(bookingsData as Booking[] || []);
        }
      } catch (error) {
        console.error('Error loading bookings:', error);
        setBookings([]);
      } finally {
        setLoading(false);
      }
    };

    loadBookings();
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Separate bookings by category
  const pendingAcceptance = bookings.filter(b => b.status === 'assigned');
  const activeJobs = bookings.filter(b => 
    ['en_route', 'arrived', 'in_progress', 'awaiting_finish_confirm', 'done_waiting_upload'].includes(b.status)
  );
  const pastServices = bookings.filter(b => 
    ['uploaded', 'completed'].includes(b.status)
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading dashboard...</p>
      </div>
    );
  }

  const userDisplayName = user?.fullName || user?.phone || 'User';
  const userAvatarUrl = user?.avatar_url;

  // Get filmmaker step from status (0-based for 3 total steps)
  const getFilmmakerStep = (status: string): number => {
    switch (status) {
      case 'assigned':
        return 0; // Not shown in active jobs, but return 0 for consistency
      case 'en_route':
        return 0;
      case 'arrived':
      case 'in_progress':
      case 'awaiting_finish_confirm':
        return 1;
      case 'done_waiting_upload':
        return 2;
      case 'uploaded':
      case 'completed':
        return 2; // Also step 2 since Shooting Done is no longer part of the active flow
      default:
        return 0;
    }
  };

  // Get step label for display
  const getStepLabel = (status: string): string => {
    switch (status) {
      case 'assigned':
        return 'Review Booking';
      case 'en_route':
        return 'Go to Client';
      case 'arrived':
      case 'in_progress':
      case 'awaiting_finish_confirm':
        return 'Shooting in Progress';
      case 'done_waiting_upload':
        return 'Upload the Shooting Material';
      case 'uploaded':
      case 'completed':
        return 'Shooting Done';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="sticky top-0 z-10 bg-white px-5 py-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-400 mb-1">Welcome!</p>
            <h1 className="text-2xl font-bold text-black">{userDisplayName}</h1>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="cursor-pointer hover:opacity-80 transition-opacity">
                <Avatar className="h-12 w-12 border border-gray-300">
                  {userAvatarUrl && <AvatarImage src={userAvatarUrl} alt={userDisplayName} />}
                  <AvatarFallback className="bg-gray-100 text-gray-600">
                    {userDisplayName.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Logout</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="px-5 space-y-6">
        {/* Pending Acceptance */}
        {pendingAcceptance.length > 0 && (
          <section className="pt-5 mb-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              Requires Your Action
              <Badge variant="destructive" className="ml-2">
                {pendingAcceptance.length}
              </Badge>
            </h2>
            <div className="flex flex-col gap-2">
              {pendingAcceptance.map((booking) => (
                <Link key={booking.id} to={`/filmmaker/jobs/${booking.id}`} className="block">
                  <Card className="p-4 hover:border-primary transition-colors border-l-4 border-l-destructive">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-base">{booking.package?.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {booking.customer?.full_name || 'Customer'}
                        </p>
                      </div>
                      <Badge 
                        className={`${statusColors[booking.status]} text-white`}
                      >
                        {statusLabels[booking.status]}
                      </Badge>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span>{format(new Date(booking.scheduled_at), 'MMM d, yyyy • h:mm a')}</span>
                      </div>
                      
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="h-4 w-4" />
                        <span className="line-clamp-1">
                          {booking.address_line1}, {booking.city}
                        </span>
                      </div>
                    </div>

                    <div className="mt-3 pt-3 border-t border-border">
                      <Button variant="outline" size="sm" className="w-full">
                        Review & Accept
                      </Button>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Your Current Active Job */}
        {(activeJobs.length > 0 || pendingAcceptance.length === 0) && (
          <section className="pt-5">
            <h2 className="text-lg font-semibold mb-4">Your Current Active Job</h2>
            {activeJobs.length > 0 ? (
              <div className="space-y-5">
                {activeJobs.map((booking) => {
                  const currentStep = getFilmmakerStep(booking.status);
                  const totalSteps = 3;
                  return (
                    <div key={booking.id}>
                      <Link to={`/filmmaker/jobs/${booking.id}`}>
                        <Card className="p-5 hover:border-primary transition-colors">
                        {/* Booking Info */}
                        <p className="text-sm text-gray-400 mb-2">
                          {format(new Date(booking.scheduled_at), 'MMM d, yyyy')}
                        </p>
                        <h3 className="font-bold text-xl text-black mb-4">{booking.package?.name || 'The Glow Session'}</h3>
                        
                        <div className="space-y-3 mb-4">
                          <div className="flex items-center gap-2 text-sm text-black">
                            <Clock className="h-4 w-4" />
                            <span>
                              {format(new Date(booking.scheduled_at), 'h:mm a')} - {format(new Date(new Date(booking.scheduled_at).getTime() + (booking.package?.duration_minutes || 0) * 60000), 'h:mm a')} ({Math.floor((booking.package?.duration_minutes || 0) / 60)}h Shoot)
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-2 text-sm text-black">
                            <MapPin className="h-4 w-4" />
                            <span className="line-clamp-1">
                              {booking.address_line1}, {booking.city}
                            </span>
                          </div>
                        </div>

                        {/* Step Progress */}
                        <div className="mb-4">
                          <p className="text-sm text-black">{getStepLabel(booking.status)}</p>
                          <StepProgressBar currentStep={currentStep + 1} totalSteps={totalSteps} className="h-2 mt-2" />
                          <p className="text-sm text-gray-400 mt-1">
                            Step {currentStep + 1} of {totalSteps}
                          </p>
                        </div>

                        {/* Customer Info */}
                        {booking.customer && (
                          <div className="flex items-center gap-3 pt-4 border-t border-gray-200 mb-4">
                            <Avatar className="h-12 w-12">
                              <AvatarFallback className="bg-gray-100 text-gray-600">
                                {booking.customer.full_name?.charAt(0).toUpperCase() || 'C'}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <p className="font-bold text-base text-black">{booking.customer.full_name}</p>
                              <div className="flex items-center gap-1">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <Star key={star} className="h-3 w-3 fill-orange-500 text-orange-500" />
                                ))}
                                <span className="text-xs text-gray-600 ml-1">4.9/5.0</span>
                              </div>
                            </div>
                          </div>
                        )}

                        <Button variant="default" size="sm" className="w-full bg-gray-800 hover:bg-gray-700 text-white font-normal">
                          View Shooting
                        </Button>
                        </Card>
                      </Link>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Card className="p-8 text-center">
                <h3 className="font-semibold text-lg mb-2">No active jobs</h3>
                <p className="text-sm text-muted-foreground">
                  You're all caught up! New assignments will appear here.
                </p>
              </Card>
            )}
          </section>
        )}

        {/* Past Services */}
        {pastServices.length > 0 && (
          <section className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Your Past Shootings</h2>
              <Link to="/filmmaker/past-services" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary">
                See All
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="space-y-2">
              {pastServices.slice(0, 4).map((booking) => (
                <div key={booking.id}>
                  <Link to={`/filmmaker/jobs/${booking.id}`}>
                    <Card className="p-0 hover:border-primary transition-colors flex">
                      <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                        <img 
                          src="https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?w=400&h=400&fit=crop" 
                          alt={booking.package?.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 p-4">
                        <h3 className="font-bold text-base mb-1">{booking.package?.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {format(new Date(booking.scheduled_at), 'MMM d, yyyy')} • ${(booking.total_cents / 100).toFixed(2)}
                        </p>
                      </div>
                    </Card>
                  </Link>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Bottom Navigation */}
      <FilmmakerBottomNav />
    </div>
  );
}
